import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function Card({ title, children, testid }) {
  return (
    <section className="surface p-5 sm:p-6" data-testid={testid}>
      <h2 className="font-display text-xl font-bold uppercase mb-4">{title}</h2>
      {children}
    </section>
  );
}

function BlockedEditor({ blocked, setBlocked }) {
  const [b, setB] = useState({ date: "", time: "", note: "" });
  const add = () => {
    if (!b.date) return;
    setBlocked([...blocked, { date: b.date, time: b.time || null, note: b.note }]);
    setB({ date: "", time: "", note: "" });
  };
  return (
    <div>
      <div className="grid sm:grid-cols-[1fr_120px_1fr_auto] gap-2">
        <input type="date" className="field mono" value={b.date} onChange={(e) => setB({ ...b, date: e.target.value })} data-testid="block-date-input" />
        <input type="time" className="field mono" value={b.time} onChange={(e) => setB({ ...b, time: e.target.value })} data-testid="block-time-input" />
        <input className="field" placeholder="Nota (es. ferie)" value={b.note} onChange={(e) => setB({ ...b, note: e.target.value })} data-testid="block-note-input" />
        <button type="button" onClick={add} className="btn-ghost px-4 flex items-center gap-1.5 text-sm" data-testid="add-block-button"><Plus size={15} /> Blocca</button>
      </div>
      <p className="text-xs text-slate-500 mt-2">Lascia l'ora vuota per bloccare l'intera giornata. L'ora deve coincidere con l'inizio di uno slot.</p>
      <div className="space-y-2 mt-4" data-testid="blocked-list">
        {blocked.map((x, i) => (
          <div key={i} className="surface-2 px-3 py-2 flex items-center gap-3 text-sm">
            <span className="mono text-white capitalize">{fmtDate(x.date, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
            <span className="tag">{x.time || "TUTTO IL GIORNO"}</span>
            <span className="text-slate-400 truncate flex-1">{x.note}</span>
            <button type="button" onClick={() => setBlocked(blocked.filter((_, j) => j !== i))} data-testid={`remove-block-${i}`}><Trash2 size={15} className="text-slate-500 hover:text-red-400" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCalendar() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/calendar").then(({ data }) => setS(data));
  }, []);
  if (!s) return <div className="text-slate-500">Caricamento…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const toggleDay = (d) => set("working_days", s.working_days.includes(d) ? s.working_days.filter((x) => x !== d) : [...s.working_days, d].sort());
  const setRange = (i, k, v) => set("time_ranges", s.time_ranges.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/calendar", s);
      setS(data);
      toast.success("Impostazioni calendario salvate");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-4xl">
      <div className="eyebrow mb-2">Configurazione</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mb-8" data-testid="calendar-settings-title">Impostazioni Calendario</h1>
      <div className="space-y-5">
        <Card title="Giorni lavorativi" testid="working-days-card">
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`workday-${i}`}
                className={`slot-btn w-16 py-2.5 text-sm font-bold ${s.working_days.includes(i) ? "active" : ""}`}>{d}</button>
            ))}
          </div>
        </Card>
        <Card title="Fasce orarie" testid="time-ranges-card">
          <div className="space-y-2">
            {s.time_ranges.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="time" className="field mono !w-auto" value={r.start} onChange={(e) => setRange(i, "start", e.target.value)} data-testid={`range-start-${i}`} />
                <span className="text-slate-500">→</span>
                <input type="time" className="field mono !w-auto" value={r.end} onChange={(e) => setRange(i, "end", e.target.value)} data-testid={`range-end-${i}`} />
                <button type="button" onClick={() => set("time_ranges", s.time_ranges.filter((_, j) => j !== i))} className="btn-ghost p-2" data-testid={`remove-range-${i}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => set("time_ranges", [...s.time_ranges, { start: "09:00", end: "12:00" }])} className="btn-ghost mt-3 px-3 py-2 text-sm flex items-center gap-1.5" data-testid="add-range-button"><Plus size={15} /> Aggiungi fascia</button>
        </Card>
        <Card title="Capacità" testid="capacity-card">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Durata slot</label>
              <select className="field mono" value={s.slot_minutes} onChange={(e) => set("slot_minutes", Number(e.target.value))} data-testid="slot-duration-select">
                {[15, 20, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} minuti</option>)}
              </select>
            </div>
            <div>
              <label className="label">Max richieste / giorno</label>
              <input type="number" min={1} className="field mono" value={s.max_per_day} onChange={(e) => set("max_per_day", Number(e.target.value))} data-testid="max-per-day-input" />
            </div>
            <div>
              <label className="label">Prenotabile fino a (giorni)</label>
              <input type="number" min={1} className="field mono" value={s.booking_days_ahead} onChange={(e) => set("booking_days_ahead", Number(e.target.value))} data-testid="days-ahead-input" />
            </div>
          </div>
        </Card>
        <Card title="Date / ore bloccate" testid="blocked-card">
          <BlockedEditor blocked={s.blocked} setBlocked={(v) => set("blocked", v)} />
        </Card>
        <button onClick={save} disabled={busy} className="btn-red px-8 py-3 flex items-center gap-2" data-testid="save-calendar-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Salva impostazioni
        </button>
      </div>
    </div>
  );
}
